import {
  legalEntityToCode,
  type PaymentInstrument,
  PaymentModuleId,
} from '@foundation/apis/payments';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { CountryFlag } from '@shared/ui';
import { useMemo, useState, useRef } from 'react';
import { BiDotsVertical } from 'react-icons/bi';

import {
  disableUIBlocker,
  enableUIBlocker,
  GlobeIcon,
  Paragraph,
  PaymentIcon,
  SecondaryButton,
} from '@/components/common';
import { InstrumentStatusBadge } from '@/components/common/badges/InstrumentStatusBadge';
import { useActionOnKeydown } from '@/hooks/useActionOnKeydown';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useUserFriendlyPrefixForInstrumentBrand } from '@/hooks/useUserFriendlyPrefixForInstrumentBrands';
import { triggerErrorToast } from '@/libs/toasts';
import NavigationService, {
  InternalPage,
} from '@/services/navigation/NavigationService';
import { type IManagePaymentInstrument } from '@/services/payments/instruments/MandateReference';
import { getMandateByReference } from '@/services/payments/instruments/PaymentInstrumentsApi';
import PaymentStore from '@/services/payments/PaymentStore';

import { EditStoredInstrumentSection } from './EditStoredInstrumentSection';

type LegalEntityFlag = ReturnType<(typeof legalEntityToCode)['get']>;

const Flag = ({
  instrument,
  flag,
}: {
  instrument: PaymentInstrument;
  flag: LegalEntityFlag | undefined;
}) => {
  return instrument.isDuplicate ? (
    <div className="grow-0 shrink-0 w-6 flex flex-row items-center h-full">
      {instrument.legalEntityNumber === 0 ? (
        //Paypal has no reference to a particular country, but value for this should be 0!
        <GlobeIcon className="w-6 h-4 object-cover" />
      ) : flag ? (
        <CountryFlag countryCode={flag.code} />
      ) : (
        '*'
      )}
    </div>
  ) : null;
};

export function ManageStoredInstrument(instrument: IManagePaymentInstrument) {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const instrumentBrandPrefixer = useUserFriendlyPrefixForInstrumentBrand();
  const [isEditSectionExpanded, setIsEditSectionExpanded] = useState(false);
  const isPendingMandate =
    instrument.mandateReference !== undefined &&
    instrument.mandateStatus === 'Pending';

  const openActivation = async () => {
    const mandateReference = instrument.mandateReference;
    if (mandateReference === undefined) {
      return;
    }

    try {
      enableUIBlocker();
      const details = await getMandateByReference(
        mandateReference,
        accessToken ?? '',
      );
      PaymentStore.mandateActivation = { mandateReference, details };
      NavigationService.navigateTo({
        page: InternalPage.SepaMandateActivation,
      });
    } catch {
      triggerErrorToast(
        t('Something went wrong!'),
        t('Failed to load mandate details.'),
      );
    } finally {
      disableUIBlocker();
    }
  };

  const instrumentName = useMemo(() => {
    if (instrument.brand) {
      const brandPrefix = instrumentBrandPrefixer(instrument.brand) ?? '';

      if (instrument.moduleId === PaymentModuleId.BraintreeGooglePay) {
        const googlePayString = 'Google Pay';
        return `${googlePayString} (${brandPrefix ?? ''} ${
          instrument.maskedInfo
        })`;
      }

      return `${brandPrefix ?? ''} ${instrument.maskedInfo}`;
    }

    return instrument.maskedInfo;
  }, [instrument, instrumentBrandPrefixer]);

  const flag =
    instrument.legalEntityNumber === undefined
      ? undefined
      : legalEntityToCode.get(instrument.legalEntityNumber);

  const popoverRef = useRef(null);
  useClickOutside(popoverRef, () => setIsEditSectionExpanded(false));
  useActionOnKeydown(
    isEditSectionExpanded,
    () => setIsEditSectionExpanded(false),
    ['Escape'],
  );
  return (
    <div
      className={'w-full relative'}
      ref={popoverRef}
    >
      <div className={'w-full flex flex-row items-center gap-xs'}>
        <PaymentIcon icon={instrument.iconName} />
        <div className="w-full flex flex-col md:flex-row md:items-center gap-[4px] md:gap-xs">
          <div className="flex flex-row w-full justify-between items-center">
            <div
              className="grid"
              title={instrumentName}
            >
              <Paragraph
                ellipsisOnOverflow
                extraClassName={isPendingMandate ? 'text-amber-700' : undefined}
              >
                {instrumentName}
              </Paragraph>
              <div className="flex items-center gap-3">
                {instrument.expirationDate && (
                  <div className="grow-0 shrink-0 w-auto text-sm text-silver flex space-x-2 items-center">
                    <Paragraph ellipsisOnOverflow>
                      {instrument.expirationDate}
                    </Paragraph>
                    <Flag
                      instrument={instrument}
                      flag={flag}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <InstrumentStatusBadge
            instrument={instrument}
            extraClassName="px-[12px] py-[6px] line-height-1 text-xs font-semibold order-first md:order-last w-fit !m-0 !md:m-auto"
          />
          {isPendingMandate && (
            <SecondaryButton
              isSmall
              flex={false}
              onClick={openActivation}
              className="order-first md:order-last w-fit cursor-pointer transition-colors hover:bg-primary hover:!text-white"
            >
              {t('Activate')}
            </SecondaryButton>
          )}
        </div>
        <button
          onClick={() => setIsEditSectionExpanded((prevState) => !prevState)}
          type="button"
          aria-label={t('Payment method actions')}
          title={t('Payment method actions')}
          aria-expanded={isEditSectionExpanded}
          className="text-xs min-w-[44px] min-h-[44px] flex items-center justify-center"
        >
          <BiDotsVertical
            size={16}
            className={'text-silver cursor-pointer'}
          />
        </button>
      </div>

      {
        <EditStoredInstrumentSection
          isVisible={isEditSectionExpanded}
          instrument={instrument}
          instrumentName={instrumentName}
          hideSection={() => setIsEditSectionExpanded(false)}
        />
      }
    </div>
  );
}
